import { useMemo } from "react"
import { type Query as TCubeQuery } from "@cubejs-client/core"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import {
  BenchmarkTimeframe,
  FunnelBenchmarks,
  FunnelType,
  BENCHMARK_CUBE_NAMES,
  COLLECTION_BENCHMARK_METRICS,
  HOMEPAGE_BENCHMARK_METRICS,
  PRODUCT_BENCHMARK_METRICS,
} from "../types"

/**
 * Maps funnel type to its respective metric mappings.
 */
const FUNNEL_METRICS: Record<FunnelType, Record<string, string>> = {
  collection: COLLECTION_BENCHMARK_METRICS,
  homepage: HOMEPAGE_BENCHMARK_METRICS,
  product: PRODUCT_BENCHMARK_METRICS,
}

/**
 * Build measure names for a given timeframe and metric.
 * E.g., for timeframe='6mo' and metric='collection_to_cart_cvr':
 *   - avg_6mo_collection_to_cart_cvr
 *   - best_14d_6mo_collection_to_cart_cvr
 *   - worst_14d_6mo_collection_to_cart_cvr
 */
const buildMeasureNames = (
  cubeName: string,
  timeframe: BenchmarkTimeframe,
  metricName: string,
): { avg: string; best: string; worst: string } => ({
  avg: `${cubeName}.avg_${timeframe}_${metricName}`,
  best: `${cubeName}.best_14d_${timeframe}_${metricName}`,
  worst: `${cubeName}.worst_14d_${timeframe}_${metricName}`,
})

/**
 * Parse Cube.js result set into FunnelBenchmarks format.
 */
const parseBenchmarkResults = (
  rawData: Record<string, unknown>[] | undefined,
  cubeName: string,
  timeframe: BenchmarkTimeframe,
  metrics: Record<string, string>,
): FunnelBenchmarks | null => {
  if (!rawData || rawData.length === 0) return null

  const row = rawData[0]
  const benchmarks: FunnelBenchmarks = {}

  for (const [metricKey, metricName] of Object.entries(metrics)) {
    const measures = buildMeasureNames(cubeName, timeframe, metricName)

    benchmarks[metricKey] = {
      avg: Number(row[measures.avg]) || 0,
      best: Number(row[measures.best]) || 0,
      worst: Number(row[measures.worst]) || 0,
    }
  }

  return benchmarks
}

export interface UseBenchmarksResult {
  /** Benchmark data keyed by metric name */
  benchmarks: FunnelBenchmarks | null
  /** Whether the benchmark query is loading */
  isLoading: boolean
  /** Error if the query failed */
  error: Error | null
}

/**
 * useBenchmarks Hook
 *
 * Fetches benchmark data from Cube.js for a specific funnel type.
 * Uses the `skip` option to avoid unnecessary API calls when benchmarks are disabled.
 *
 * @param funnelType - The type of funnel ('collection', 'homepage', 'product')
 * @param sessionFilter - The session filter to apply ('TOTAL', 'ORGANIC', 'PAID', 'TRIGGERED')
 * @param timeframe - The benchmark timeframe ('1mo', '3mo', '6mo')
 * @param enabled - Whether benchmarks are enabled (if false, query is skipped)
 * @param deviceCategory - Optional device category filter ('ALL', 'DESKTOP', 'MOBILE', 'TABLET')
 *
 * @returns {UseBenchmarksResult} - Benchmark data, loading state, and error
 */
export function useBenchmarks(
  funnelType: FunnelType,
  sessionFilter: string,
  timeframe: BenchmarkTimeframe,
  enabled: boolean,
  deviceCategory?: string,
): UseBenchmarksResult {
  const currentCompany = useCurrentCompany()
  const companyId = currentCompany?.id

  const cubeName = BENCHMARK_CUBE_NAMES[funnelType]
  const metrics = FUNNEL_METRICS[funnelType]

  // Build the query with all measures for the selected timeframe
  const query: TCubeQuery = useMemo(() => {
    const measures: string[] = []

    for (const metricName of Object.values(metrics)) {
      const measureNames = buildMeasureNames(cubeName, timeframe, metricName)
      measures.push(measureNames.avg, measureNames.best, measureNames.worst)
    }

    const filters: TCubeQuery["filters"] = [
      {
        member: `${cubeName}.companyid`,
        operator: "equals",
        values: companyId ? [companyId] : [],
      },
      {
        member: `${cubeName}.session_filter`,
        operator: "equals",
        values: [sessionFilter],
      },
    ]

    // Add device_category filter when provided
    if (deviceCategory && deviceCategory !== "ALL") {
      filters.push({
        member: `${cubeName}.device_category`,
        operator: "equals",
        values: [deviceCategory.toLowerCase()],
      })
    }

    return {
      measures,
      dimensions: [`${cubeName}.session_filter`],
      filters,
    }
  }, [cubeName, timeframe, metrics, companyId, sessionFilter, deviceCategory])

  // Skip query when benchmarks are disabled or company ID is not available
  const shouldSkip = !enabled || !companyId

  const { resultSet, isLoading, error } = useCubeQuery(query, {
    skip: shouldSkip,
  })

  // Parse the results into FunnelBenchmarks format
  const benchmarks = useMemo(() => {
    if (!resultSet || shouldSkip) return null
    return parseBenchmarkResults(
      resultSet.rawData(),
      cubeName,
      timeframe,
      metrics,
    )
  }, [resultSet, cubeName, timeframe, metrics, shouldSkip])

  return {
    benchmarks,
    isLoading: Boolean(isLoading),
    error,
  }
}

export default useBenchmarks
